function toNumber(value, fallback = 0) {
  return Number.isFinite(value) ? Number(value) : fallback;
}

export function planInstructionalTimeline(events) {
  const list = Array.isArray(events) ? events : [];
  if (list.length === 0) return [];

  const byId = new Map(list.map((evt) => [evt.id, evt]));
  const indegree = new Map();
  const children = new Map();

  for (const evt of list) {
    indegree.set(evt.id, 0);
    children.set(evt.id, []);
  }

  for (const evt of list) {
    const deps = Array.isArray(evt.deps) ? evt.deps : [];
    for (const depId of deps) {
      if (!byId.has(depId)) continue;
      indegree.set(evt.id, (indegree.get(evt.id) || 0) + 1);
      children.get(depId).push(evt.id);
    }
  }

  const queue = [];
  for (const evt of list) {
    if ((indegree.get(evt.id) || 0) === 0) queue.push(evt.id);
  }

  const ordered = [];
  while (queue.length > 0) {
    const id = queue.shift();
    ordered.push(id);
    for (const childId of children.get(id) || []) {
      indegree.set(childId, (indegree.get(childId) || 0) - 1);
      if ((indegree.get(childId) || 0) === 0) queue.push(childId);
    }
  }

  // Cycle fallback: preserve input order for unresolved nodes.
  if (ordered.length !== list.length) {
    for (const evt of list) {
      if (!ordered.includes(evt.id)) ordered.push(evt.id);
    }
  }

  const levelById = new Map();
  for (const id of ordered) {
    const evt = byId.get(id);
    const deps = Array.isArray(evt?.deps) ? evt.deps : [];
    const level = deps.reduce((acc, depId) => Math.max(acc, (levelById.get(depId) || 0) + 1), 0);
    levelById.set(id, level);
  }

  return ordered.map((id, orderIndex) => ({
    ...byId.get(id),
    orderIndex,
    timelineGroup: levelById.get(id) || 0,
  }));
}

export function planAnimationTimeline(steps) {
  const normalized = (Array.isArray(steps) ? steps : [])
    .filter((step) => step && typeof step === 'object')
    .map((step, index) => ({
      ...step,
      step: Number.isFinite(step.step) ? step.step : index + 1,
      durationSec: Math.max(0, toNumber(step.durationSec, 0.25)),
      deps: Array.isArray(step.deps)
        ? step.deps.filter((id) => Number.isFinite(id)).map((id) => Number(id))
        : [],
      overlapSec: Math.max(0, toNumber(step.overlapSec, 0)),
      startAfterSec: Math.max(0, toNumber(step.startAfterSec, 0)),
      waitForSignal: Boolean(step.waitForSignal),
      checkpoint: Boolean(step.checkpoint),
    }))
    .sort((a, b) => a.step - b.step);

  if (normalized.length === 0) return [];

  const byStep = new Map(normalized.map((step) => [step.step, step]));
  const schedule = [];
  const byIdTiming = new Map();
  let cursor = 0;

  for (const step of normalized) {
    const depEnd = step.deps.reduce((acc, depId) => {
      const depTiming = byIdTiming.get(depId);
      return Math.max(acc, depTiming ? depTiming.endSec : 0);
    }, 0);
    const seqStart = Math.max(0, cursor - step.overlapSec);
    const baseStart = Math.max(depEnd, seqStart) + step.startAfterSec;
    const startSec = Number(baseStart.toFixed(4));
    const endSec = Number((startSec + step.durationSec).toFixed(4));
    const planned = {
      ...step,
      startSec,
      endSec,
      deps: step.deps.filter((depId) => byStep.has(depId)),
    };
    schedule.push(planned);
    byIdTiming.set(step.step, planned);
    cursor = Math.max(cursor, endSec);
  }

  return schedule;
}
